import {
    Component,
    OnInit,
    ViewChild,
} from '@angular/core';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { GlobalConstants } from '../../common/global-constants';
import {
    ChartComponent,
    ApexNonAxisChartSeries,
    ApexResponsive,
    ApexChart,
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexTitleSubtitle,
    ApexFill,
    ApexStroke,
    ApexYAxis,
    ApexXAxis,
    ApexLegend,
    ApexGrid,
    ApexPlotOptions,
    NgApexchartsModule,
    ApexTooltip,
} from 'ng-apexcharts';

export type RiskChartOptions = {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    responsive: ApexResponsive[];
    labels: any;
    title: ApexTitleSubtitle;
    fill: ApexFill;
    tooltip: ApexTooltip;
    yaxis: ApexYAxis;
    stroke: ApexStroke;
    legend: ApexLegend;
    colors: string[];
    plotOptions: ApexPlotOptions;
};

export type DomainChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    dataLabels: ApexDataLabels;
    plotOptions: ApexPlotOptions;
    yaxis: ApexYAxis;
    xaxis: ApexXAxis;
    grid: ApexGrid;
    colors: string[];
    legend: ApexLegend;
};

@Component({
    selector: 'app-i-active-directory',
    standalone: true,
    imports: [NgClass, NgFor, NgIf, MatCardModule, MatTabsModule, NgApexchartsModule],
    templateUrl: './i-active-directory.component.html',
    styleUrl: './i-active-directory.component.scss',
})
export class IActiveDirectoryComponent implements OnInit {
    // isToggled
    isToggled = false;

    // Referencias a los elementos <canvas> en el HTML
    @ViewChild('riskChart') riskChartRef: ChartComponent;
    @ViewChild('domainChart') domainChartRef: ChartComponent;
    public riskChartOptions: Partial<RiskChartOptions>;
    public domainChartOptions: Partial<DomainChartOptions>;

    constructor(public themeService: CustomizerSettingsService) {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });

        this.riskChartOptions = {
            series: [this.immediateCount, this.potentialCount, this.minorCount],
            chart: {
                height: 400,
                type: 'donut',
                toolbar: {
                    show: true,
                },
            },
            labels: ['Crítico', 'Alto', 'Medio'],
            fill: {
                opacity: 1,
            },
            stroke: {
                width: 1,
                colors: undefined,
            },
            colors: [
                this.altairColors.error,
                this.altairColors.warning,
                this.altairColors.medium,
            ],
            yaxis: {
                show: false,
            },
            legend: {
                offsetY: -10,
                fontSize: '14px',
                position: 'bottom',
                horizontalAlign: 'center',
                labels: {
                    colors: '#919aa3',
                },
                itemMargin: {
                    horizontal: 12,
                    vertical: 7,
                },
                formatter: function (seriesName, opts) {
                    return (
                        seriesName +
                        ' - ' +
                        opts.w.globals.series[opts.seriesIndex]
                    );
                },
            },
            plotOptions: {
                pie: {
                    donut: {
                        labels: {
                            show: true,
                            name: {
                                show: true,
                            },
                            value: {
                                show: true,
                            },
                            total: {
                                show: true,
                                showAlways: true,
                            },
                        },
                    },
                },
            },
            tooltip: {
                y: {
                    formatter: function (val) {
                        return val + '';
                    },
                },
            },
        };

        this.domainChartOptions = {
            series: [
                {
                    name: 'Cantidad',
                    data: [
                        this.domainStats.nb_users,
                        this.domainStats.nb_groups,
                        this.domainStats.nb_computers,
                        this.domainStats.nb_dc,
                        this.domainStats.nb_da,
                    ],
                },
            ],
            chart: {
                height: 350,
                type: 'bar',
                events: {
                    click: function (chart, w, e) {
                        // console.log(chart, w, e)
                    },
                },
            },
            colors: [
                this.altairColors.primary,
                this.altairColors.success,
                this.altairColors.warning,
                this.altairColors.medium,
                this.altairColors.error,
            ],
            plotOptions: {
                bar: {
                    columnWidth: '45%',
                    distributed: true,
                },
            },
            dataLabels: {
                enabled: false,
            },
            legend: {
                offsetY: 5,
                show: false,
                fontSize: '14px',
                position: 'bottom',
                horizontalAlign: 'center',
                labels: {
                    colors: '#919aa3',
                },
                itemMargin: {
                    horizontal: 10,
                    vertical: 5,
                },
            },
            grid: {
                show: true,
                strokeDashArray: 5,
                borderColor: '#e0e0e0',
                row: {
                    colors: ['#f4f6fc', 'transparent'], // takes an array which will be repeated on columns
                    opacity: 0.5,
                },
            },
            xaxis: {
                categories: [
                    'Usuarios',
                    'Grupos',
                    'Computadoras',
                    'DCs',
                    'Admins',
                ],
                labels: {
                    show: true,
                    style: {
                        colors: [
                            this.altairColors.primary,
                            this.altairColors.success,
                            this.altairColors.warning,
                            this.altairColors.medium,
                            this.altairColors.error,
                        ],
                        fontSize: '14px',
                    },
                },
                axisBorder: {
                    show: false,
                    color: '#e0e0e0',
                },
                axisTicks: {
                    show: true,
                    color: '#e0e0e0',
                },
            },
            yaxis: {
                labels: {
                    show: true,
                    style: {
                        colors: '#919aa3',
                        fontSize: '14px',
                    },
                },
                axisBorder: {
                    show: false,
                },
            },
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for role name
     */
    get roleName(): string {
        return localStorage.getItem('roleName') ?? '';
    }

    /**
     * Getter for company name
     */
    get companyName(): string {
        return localStorage.getItem('companyName') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    // --- Estado del Componente y Datos ---
    isModalOpen: boolean = false;
    modalTitle: string = '';
    modalBodyContent: any;

    // Contadores dinámicos
    immediateCount: number = 0;
    potentialCount: number = 0;
    minorCount: number = 0;
    handledCount: number = 0;

    // Datos reales extraídos del HTML (TypeScript tipado)
    adMinerData = {
        dangerous_paths: 8,
        privileged_accounts_outside_Protected_Users: 4,
        graph_list_objects_rbcd: 1,
        krb_last_change: 2335,
        computers_last_connexion: 37,
        server_users_could_be_admin: 1,
        rid_singularities: 24,
        computers_without_laps: 100,
        users_pwd_not_changed_since: 23,
        dormants_accounts: 10,
        empty_groups: 13,
        never_expires: 19,
        anomaly_acl: 7,
        vuln_functional_level: 1,
        empty_ous: 2,
    };

    domainStats = {
        nb_domains: 1,
        nb_dc: 1,
        nb_da: 4,
        nb_users: 32,
        nb_groups: 55,
        nb_computers: 65,
        nb_adcs: 0,
    };

    altairColors = {
        primary: '#072E48',
        success: '#00C688',
        error: '#B91C1C',
        warning: '#D97706',
        medium: '#fbbf24',
        text: '#686E7A',
    };

    problemCategories = {
        critical: [
            'dangerous_paths',
            'privileged_accounts_outside_Protected_Users',
        ],
        warning: [
            'graph_list_objects_rbcd',
            'krb_last_change',
            'computers_last_connexion',
            'server_users_could_be_admin',
            'rid_singularities',
        ],
        minor: [
            'computers_without_laps',
            'users_pwd_not_changed_since',
            'dormants_accounts',
            'empty_groups',
            'never_expires',
        ],
    };

    // Datos de las tablas (simulados)
    usersData = [
        {
            name: 'Administrator',
            status: 'Activo',
            lastAccess: '2025-10-07',
            privileges: 'Admin',
            groups: 'Domain Admins',
        },
        {
            name: 'Guest',
            status: 'Inactivo',
            lastAccess: 'Nunca',
            privileges: 'Usuario',
            groups: 'Guests',
        },
        {
            name: 'krbtgt',
            status: 'Activo',
            lastAccess: '2018-01-01',
            privileges: 'Sistema',
            groups: 'Domain Users',
        },
        {
            name: 'sistemas',
            status: 'Activo',
            lastAccess: '2025-10-06',
            privileges: 'Admin',
            groups: 'Domain Admins',
        },
        {
            name: 'usuario1',
            status: 'Activo',
            lastAccess: '2025-10-05',
            privileges: 'Usuario',
            groups: 'Domain Users',
        },
        {
            name: 'usuario2',
            status: 'Inactivo',
            lastAccess: '2025-09-15',
            privileges: 'Usuario',
            groups: 'Domain Users',
        },
        {
            name: 'servicio1',
            status: 'Activo',
            lastAccess: '2025-10-07',
            privileges: 'Servicio',
            groups: 'Service Accounts',
        },
        {
            name: 'backup_admin',
            status: 'Activo',
            lastAccess: '2025-10-01',
            privileges: 'Admin',
            groups: 'Backup Operators',
        },
    ];

    groupsData = [
        {
            name: 'Domain Admins',
            type: 'Seguridad',
            members: 4,
            status: 'Activo',
            description: 'Administradores del dominio',
        },
        {
            name: 'Domain Users',
            type: 'Seguridad',
            members: 28,
            status: 'Activo',
            description: 'Usuarios del dominio',
        },
        {
            name: 'Backup Operators',
            type: 'Seguridad',
            members: 2,
            status: 'Activo',
            description: 'Operadores de respaldo',
        },
        {
            name: 'Print Operators',
            type: 'Seguridad',
            members: 0,
            status: 'Vacío',
            description: 'Operadores de impresión',
        },
        {
            name: 'Server Operators',
            type: 'Seguridad',
            members: 1,
            status: 'Activo',
            description: 'Operadores de servidor',
        },
        {
            name: 'Account Operators',
            type: 'Seguridad',
            members: 0,
            status: 'Vacío',
            description: 'Operadores de cuentas',
        },
        {
            name: 'Guests',
            type: 'Seguridad',
            members: 1,
            status: 'Activo',
            description: 'Invitados',
        },
        {
            name: 'Users',
            type: 'Seguridad',
            members: 15,
            status: 'Activo',
            description: 'Usuarios locales',
        },
    ];

    computersData = [
        {
            name: 'DC01',
            os: 'Windows Server 2019',
            lastConnection: '2025-10-07',
            laps: 'No',
            status: 'Activo',
        },
        {
            name: 'WS001',
            os: 'Windows 10',
            lastConnection: '2025-10-06',
            laps: 'No',
            status: 'Activo',
        },
        {
            name: 'WS002',
            os: 'Windows 10',
            lastConnection: '2025-09-15',
            laps: 'No',
            status: 'Inactivo',
        },
        {
            name: 'SRV01',
            os: 'Windows Server 2016',
            lastConnection: '2025-10-05',
            laps: 'No',
            status: 'Activo',
        },
        {
            name: 'WS003',
            os: 'Windows 11',
            lastConnection: '2025-10-07',
            laps: 'No',
            status: 'Activo',
        },
        {
            name: 'LAPTOP01',
            os: 'Windows 10',
            lastConnection: '2025-08-20',
            laps: 'No',
            status: 'Inactivo',
        },
        {
            name: 'LAPTOP02',
            os: 'Windows 11',
            lastConnection: '2025-10-01',
            laps: 'No',
            status: 'Activo',
        },
        {
            name: 'WS004',
            os: 'Windows 10',
            lastConnection: '2025-07-10',
            laps: 'No',
            status: 'Inactivo',
        },
    ];

    securityData = [
        {
            problem: 'Rutas de ataque peligrosas',
            severity: 'Crítico',
            count: 8,
            impact: 'Alto',
            status: 'Pendiente',
        },
        {
            problem: 'Cuentas privilegiadas desprotegidas',
            severity: 'Crítico',
            count: 4,
            impact: 'Alto',
            status: 'Pendiente',
        },
        {
            problem: 'Kerberos RBCD',
            severity: 'Alto',
            count: 1,
            impact: 'Medio',
            status: 'Pendiente',
        },
        {
            problem: 'Contraseña KRBTGT antigua',
            severity: 'Alto',
            count: 1,
            impact: 'Alto',
            status: 'Pendiente',
        },
        {
            problem: 'Computadoras sin conexión',
            severity: 'Alto',
            count: 37,
            impact: 'Medio',
            status: 'Pendiente',
        },
        {
            problem: 'Usuarios con privilegios de servidor',
            severity: 'Alto',
            count: 1,
            impact: 'Medio',
            status: 'Pendiente',
        },
        {
            problem: 'Singularidades RID',
            severity: 'Alto',
            count: 24,
            impact: 'Bajo',
            status: 'Pendiente',
        },
        {
            problem: 'Computadoras sin LAPS',
            severity: 'Medio',
            count: 65,
            impact: 'Medio',
            status: 'Pendiente',
        },
        {
            problem: 'Contraseñas no cambiadas',
            severity: 'Medio',
            count: 23,
            impact: 'Medio',
            status: 'Pendiente',
        },
        {
            problem: 'Cuentas inactivas',
            severity: 'Medio',
            count: 10,
            impact: 'Bajo',
            status: 'Pendiente',
        },
        {
            problem: 'Grupos vacíos',
            severity: 'Medio',
            count: 13,
            impact: 'Bajo',
            status: 'Pendiente',
        },
        {
            problem: 'Contraseñas sin expiración',
            severity: 'Medio',
            count: 19,
            impact: 'Medio',
            status: 'Pendiente',
        },
    ];

    // --- Ciclos de Vida ---

    ngOnInit(): void {
        this.updateCounters();
    }

    // --- Métodos de Lógica de Negocio ---

    private updateCounters(): void {
        const criticalCount = this.problemCategories.critical.filter(
            (issue) =>
                this.adMinerData[issue as keyof typeof this.adMinerData] > 0
        ).length;
        const warningCount = this.problemCategories.warning.filter(
            (issue) =>
                this.adMinerData[issue as keyof typeof this.adMinerData] > 0
        ).length;
        const minorCount = this.problemCategories.minor.filter(
            (issue) =>
                this.adMinerData[issue as keyof typeof this.adMinerData] > 0
        ).length;

        const totalIssues = Object.keys(this.adMinerData).length;
        const activeIssues = criticalCount + warningCount + minorCount;
        const handledCount = totalIssues - activeIssues;

        this.immediateCount = criticalCount;
        this.potentialCount = warningCount;
        this.minorCount = minorCount;
        this.handledCount = handledCount;
    }

    private initializeCharts(): void {
        // Gráfica de distribución de riesgos por severidad
        if (this.riskChartRef) {
            //const riskCtx = this.riskChartRef.nativeElement.getContext('2d');
            /*new Chart(riskCtx!, {
                type: 'doughnut',
                data: {
                    labels: ['Crítico', 'Alto', 'Medio'],
                    datasets: [
                        {
                            data: [
                                this.immediateCount,
                                this.potentialCount,
                                this.minorCount,
                            ],
                            backgroundColor: [
                                this.altairColors.error,
                                this.altairColors.warning,
                                this.altairColors.medium,
                            ],
                            borderWidth: 2,
                            borderColor: '#ffffff',
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: {
                                padding: 20,
                                font: { family: 'Montserrat', size: 12 },
                            },
                        },
                        tooltip: {
                            backgroundColor: this.altairColors.primary,
                            titleFont: { family: 'Poppins' },
                            bodyFont: { family: 'Montserrat' },
                            callbacks: {
                                label: (context: any) => {
                                    const total = context.dataset.data.reduce(
                                        (a: number, b: number) => a + b,
                                        0
                                    );
                                    const percentage = (
                                        (context.parsed * 100) /
                                        total
                                    ).toFixed(1);
                                    return `${context.label}: ${context.parsed} problemas (${percentage}%)`;
                                },
                            },
                        },
                    },
                },
            });*/
        }

        // Gráfica de estado general del dominio
        if (this.domainChartRef) {
            //const domainCtx = this.domainChartRef.nativeElement.getContext('2d');
            /*new Chart(domainCtx!, {
                type: 'bar',
                data: {
                    labels: [
                        'Usuarios',
                        'Grupos',
                        'Computadoras',
                        'DCs',
                        'Admins',
                    ],
                    datasets: [
                        {
                            label: 'Cantidad',
                            data: [
                                this.domainStats.nb_users,
                                this.domainStats.nb_groups,
                                this.domainStats.nb_computers,
                                this.domainStats.nb_dc,
                                this.domainStats.nb_da,
                            ],
                            backgroundColor: [
                                this.altairColors.primary,
                                this.altairColors.success,
                                this.altairColors.warning,
                                this.altairColors.medium,
                                this.altairColors.error,
                            ],
                            borderColor: this.altairColors.primary,
                            borderWidth: 1,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            backgroundColor: this.altairColors.primary,
                            titleFont: { family: 'Poppins' },
                            bodyFont: { family: 'Montserrat' },
                        },
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: { font: { family: 'Montserrat' } },
                        },
                        x: { ticks: { font: { family: 'Montserrat' } } },
                    },
                },
            });*/
        }
    }

    showIssueDetail(
        severity: string,
        title: string,
        description: string
    ): void {
        this.modalTitle = title;
        this.modalBodyContent = {
            severity: severity.toUpperCase(),
            description: description,
            recommendation:
                'Se recomienda revisar y remediar este problema siguiendo las mejores prácticas de seguridad de Active Directory.',
            impact: 'Este problema podría permitir a atacantes obtener acceso no autorizado o escalar privilegios en el dominio.',
        };
        this.isModalOpen = true;
    }

    showStatDetail(type: string): void {
        let title = '';
        let content: any = {};

        switch (type) {
            case 'domain':
                title = 'Información del Dominio';
                content = {
                    'Nombre del Dominio': 'fcar.local',
                    'Nivel Funcional': 'Windows Server 2016',
                    Estado: 'Activo y Operacional',
                    'Fecha de Creación': 'Información no disponible',
                };
                break;
            case 'dc':
                title = 'Controladores de Dominio';
                content = {
                    'Total de DCs': '1 Controlador',
                    Estado: 'Operacional',
                    Replicación: 'N/A (Un solo DC)',
                    Recomendación:
                        'Considerar implementar un segundo DC para redundancia',
                };
                break;
            case 'da':
                title = 'Administradores de Dominio';
                content = {
                    'Total de DAs': `${this.domainStats.nb_da} Administradores`,
                    'Cuentas Protegidas': '0 de 4 en Protected Users',
                    Recomendación:
                        'Mover todas las cuentas administrativas al grupo Protected Users',
                };
                break;
            case 'adcs':
                title = 'Servicios de Certificados';
                content = {
                    'Servidores ADCS': `${this.domainStats.nb_adcs} Servidores`,
                    Estado: 'No implementado',
                    Recomendación:
                        'Considerar implementar PKI para mayor seguridad',
                };
                break;
        }

        this.modalTitle = title;
        this.modalBodyContent = content;
        this.isModalOpen = true;
    }

    closeModal(): void {
        this.isModalOpen = false;
    }

    // Utilidad para mapear colores a severidad en el HTML
    getSeverityBadgeClass(severity: string): string {
        switch (severity.toLowerCase()) {
            case 'crítico':
                return 'status-inactive';
            case 'alto':
                return 'status-warning';
            case 'medio':
                return 'status-warning';
            default:
                return 'status-active';
        }
    }

    // Utilidad para convertir objeto a array para *ngFor
    objectKeys(obj: object): string[] {
        return Object.keys(obj);
    }
}
